import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, X, ExternalLink, Activity } from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import KPICard from '../../../components/KPICard';
import IntelAxisTablePagination from '../../../components/mi/IntelAxisTablePagination';
import MiTableFiltersDrawer from '../../../components/mi/MiTableFiltersDrawer';
import { getPulseFeedOptions, listPulseFeed } from '../../../api/nexus';
import { assignFilterArray } from '../../../utils/filtersApi';
import { normalizeAdMediaUrl } from '../../../utils/adMediaUrl';
import { formatTableValue } from '../../../utils/intelAxisTableUtils';
import PulseActivityChart from '../PulseActivityChart';
import { formatPulseWhen } from '../pulseTimeUtils';
import { SOURCE_CHIPS } from '../pulseSourceConfig';
import '../NexusPulse.css';

const DAY_OPTIONS = [
  { label: 'All time', value: '' },
  { label: 'Last 7 days', value: '7' },
  { label: 'Last 30 days', value: '30' },
  { label: 'Last 90 days', value: '90' },
];

const isRecentPulseEvent = (event) => {
  if (!event?.occurredAt) return false;
  const rel = String(event.relativeTime || '').toLowerCase();
  if (/(just now|m ago|h ago|min ago|hour ago)/.test(rel)) return true;
  const ageMs = Date.now() - new Date(event.occurredAt).getTime();
  return ageMs >= 0 && ageMs < 48 * 60 * 60 * 1000;
};

function groupEventsByDate(events) {
  return events.reduce((groups, event) => {
    if (!event.occurredAt) return groups;
    const date = new Date(event.occurredAt).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    if (!groups[date]) groups[date] = [];
    groups[date].push(event);
    return groups;
  }, {});
}

function groupEventsByCompany(events) {
  const groups = events.reduce((acc, event) => {
    const key = String(event.company || '').trim() || 'Unknown company';
    if (!acc[key]) acc[key] = [];
    acc[key].push(event);
    return acc;
  }, {});
  return Object.keys(groups)
    .sort((a, b) => a.localeCompare(b))
    .reduce((sorted, key) => {
      sorted[key] = groups[key];
      return sorted;
    }, {});
}

function buildEventChips(event) {
  const chips = [];
  const p = event.payload || {};
  if (p.media_type) chips.push({ label: String(p.media_type) });
  if (p.filing_status) chips.push({ label: String(p.filing_status) });
  if (p.state) chips.push({ label: String(p.state) });
  if (event.eventType && !chips.some((c) => c.label === event.eventType)) {
    chips.push({ label: String(event.eventType).replace(/_/g, ' ') });
  }
  return chips;
}

function PulseEventRow({ event, navigate }) {
  const chips = buildEventChips(event);
  const live = isRecentPulseEvent(event);
  const previewSrc = normalizeAdMediaUrl(event.previewUrl);
  const when = formatPulseWhen(event.occurredAt);
  const whenPrimary = event.relativeTime || when.primary;
  const whenSecondary = event.relativeTime
    ? (when.isDateOnly ? when.primary : null)
    : when.secondary;

  return (
    <tr className={live ? 'nexus-pulse-tr--live' : ''}>
      <td>
        <span className="nexus-pulse-source-pill">
          <span
            className="nexus-pulse-source-dot"
            style={{ background: event.sourceColor || 'var(--text3)' }}
            aria-hidden
          />
          {event.sourceLabel}
        </span>
      </td>
      <td className="company-cell">
        <div className="company-name">{event.company || '—'}</div>
      </td>
      <td className="nexus-pulse-event-col">
        <div className="nexus-pulse-event-cell">
          {previewSrc ? (
            <div className="nexus-pulse-thumb-wrap">
              <img src={previewSrc} alt="" loading="lazy" decoding="async" />
            </div>
          ) : null}
          <div className="nexus-pulse-event-text">
            <div className="nexus-pulse-event-title">
              {formatTableValue(event.title || 'Untitled', 72)}
            </div>
            {event.summary ? (
              <div className="nexus-pulse-event-summary">
                {formatTableValue(event.summary, 90)}
              </div>
            ) : null}
            {chips.length > 0 ? (
              <div className="nexus-pulse-chips">
                {chips.map((chip) => (
                  <span key={chip.label} className="nexus-pulse-chip">
                    {chip.label}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </td>
      <td className="date-cell nexus-pulse-when-cell">
        <div className="nexus-pulse-when-primary">{whenPrimary}</div>
        {whenSecondary ? (
          <div className="nexus-pulse-when-secondary">{whenSecondary}</div>
        ) : null}
      </td>
      <td className="nexus-pulse-actions-cell">
        <div className="nexus-pulse-row-actions">
          {event.detailPath ? (
            <button
              type="button"
              className="btn btn-sm"
              onClick={(e) => {
                e.stopPropagation();
                navigate(event.detailPath);
              }}
            >
              More info
            </button>
          ) : null}
          {event.url ? (
            <a
              href={event.url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink size={12} />
            </a>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

export default function NexusPulse() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSource, setFilterSource] = useState('All sources');
  const [filterCompany, setFilterCompany] = useState('All companies');
  const [filterDays, setFilterDays] = useState('');
  const [showTableFilters, setShowTableFilters] = useState(false);
  const [options, setOptions] = useState({ companies: ['All companies'] });
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [pageInput, setPageInput] = useState('1');
  const [totalRows, setTotalRows] = useState(0);
  const [sourceCounts, setSourceCounts] = useState({});
  const [todayCount, setTodayCount] = useState(0);
  const [activityChart, setActivityChart] = useState([]);
  const [activityChartDays, setActivityChartDays] = useState(7);
  const [groupBy, setGroupBy] = useState('date');
  const [openFilter, setOpenFilter] = useState(null);
  const companyCatalog = useMemo(
    () => options.companies.filter((name) => name && name !== 'All companies'),
    [options.companies],
  );

  useEffect(() => {
    getPulseFeedOptions()
      .then((res) => {
        const data = res?.data?.data || {};
        setOptions({ companies: data.companies || ['All companies'] });
      })
      .catch((err) => console.error('[nexus-pulse] options failed', err));
  }, []);

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const body = {
      page: currentPage,
      limit: rowsPerPage,
      source: filterSource,
      ...(searchTerm ? { search: searchTerm } : {}),
      ...(filterDays ? { days: parseInt(filterDays, 10) } : {}),
    };
    assignFilterArray(body, 'company', filterCompany, 'All companies', companyCatalog);

    const timer = setTimeout(() => {
      listPulseFeed(body)
        .then((res) => {
          if (cancelled) return;
          setEvents(res?.data?.data || []);
          const meta = res?.data?.meta || {};
          setTotalRows(meta.totalItems || 0);
          setSourceCounts(meta.sourceCounts || {});
          setTodayCount(meta.todayCount || 0);
          setActivityChart(meta.activityChart || []);
          setActivityChartDays(meta.activityChartDays || 7);
        })
        .catch((err) => {
          console.error('[nexus-pulse] feed failed', err);
          if (!cancelled) {
            setEvents([]);
            setTotalRows(0);
            setSourceCounts({});
            setTodayCount(0);
            setActivityChart([]);
            setActivityChartDays(7);
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    currentPage,
    rowsPerPage,
    searchTerm,
    filterSource,
    filterCompany,
    filterDays,
    companyCatalog,
  ]);

  const getCountForSource = (label) => {
    if (label === 'All sources') return totalRows;
    const chip = SOURCE_CHIPS.find((c) => c.label === label);
    return chip?.code ? (sourceCounts[chip.code] || 0) : 0;
  };

  const groupedEvents = useMemo(
    () => (groupBy === 'company' ? groupEventsByCompany(events) : groupEventsByDate(events)),
    [events, groupBy],
  );

  const hasFiltersApplied = filterCompany !== 'All companies'
    || Boolean(searchTerm.trim())
    || filterDays !== ''
    || filterSource !== 'All sources';

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilterCompany('All companies');
    setFilterDays('');
    setFilterSource('All sources');
    setCurrentPage(1);
  };

  const handlePageInputSubmit = (e) => {
    e.preventDefault();
    const next = parseInt(pageInput, 10);
    const totalPages = Math.ceil(totalRows / rowsPerPage) || 1;
    if (Number.isFinite(next) && next >= 1 && next <= totalPages) {
      setCurrentPage(next);
    } else {
      setPageInput(String(currentPage));
    }
  };

  const adsCount = (sourceCounts.linkedin_ads || 0)
    + (sourceCounts.meta_ads || 0)
    + (sourceCounts.google_ads || 0);

  const daysLabel = DAY_OPTIONS.find((o) => o.value === filterDays)?.label || 'All time';

  return (
    <>
      <PageHeader
        title="Nexus"
        subtitle={
          <span className="nexus-pulse-subtitle">
            {totalRows.toLocaleString()} events
            {filterDays ? ` · last ${filterDays} days` : ''}
            {' · '}
            cross-source pulse
          </span>
        }
      >
        <div className="filters-bar page-header-filters">
          <div className="search-wrap">
            <Search className="search-icon" size={14} />
            <input
              className="search-input"
              type="text"
              placeholder="Search title, company, or ref…"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
          {hasFiltersApplied ? (
            <button type="button" className="btn btn-sm btn-clear-filters" onClick={handleClearFilters}>
              <X size={14} className="btn-clear-filters-icon" />
              <span>Clear Filters</span>
            </button>
          ) : null}
          <div className="filter-btn-wrap">
            <button
              type="button"
              className={`btn btn-sm ${showTableFilters ? 'btn-primary' : ''} btn-filters`}
              onClick={() => setShowTableFilters((v) => !v)}
            >
              <Filter size={14} />
              <span>Filters</span>
              {hasFiltersApplied ? (
                <span
                  className="btn-filters-dot"
                  style={{ background: showTableFilters ? '#fff' : 'var(--c-black)' }}
                />
              ) : null}
            </button>
          </div>
        </div>
      </PageHeader>

      <div className="body">
        <div className="feed-area nexus-pulse-feed insights-panel">
          <div className="kpi-banner-grid nexus-pulse-kpis">
            <KPICard
              title="Today"
              value={String(todayCount)}
              subtitle="New activity in the last 24 hours"
              staggerIndex={0}
              cardClassName={todayCount > 0 ? 'nexus-pulse-kpi--live' : ''}
            />
            <KPICard
              title="SERFF"
              value={String(sourceCounts.serff || 0)}
              subtitle="Filings in current filter"
              staggerIndex={1}
            />
            <KPICard
              title="News / PR"
              value={String(sourceCounts.pr_news || 0)}
              subtitle="Articles in current filter"
              staggerIndex={2}
            />
            <KPICard
              title="Ads"
              value={String(adsCount)}
              subtitle="LinkedIn, Meta, and Google"
              staggerIndex={3}
            />
            <KPICard
              title="Total events"
              value={String(totalRows)}
              subtitle="Matching active filters"
              staggerIndex={4}
            />
          </div>

          <PulseActivityChart activityChart={activityChart} windowDays={activityChartDays} />

          <div className="filters-bar nexus-pulse-filters-bar">
            <div className={`select-wrap nexus-pulse-days-select ${openFilter === 'days' ? 'is-open' : ''}`}>
              <span className="select-measure">{daysLabel}</span>
              <select
                className="filter-select"
                value={filterDays}
                aria-label="Time range"
                onFocus={() => setOpenFilter('days')}
                onBlur={() => setOpenFilter(null)}
                onChange={(e) => {
                  setFilterDays(e.target.value);
                  setCurrentPage(1);
                  e.target.blur();
                }}
              >
                {DAY_OPTIONS.map((opt) => (
                  <option key={opt.label} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="nexus-pulse-group-toggle" role="group" aria-label="Group events by">
              <button
                type="button"
                className={`nexus-pulse-group-btn ${groupBy === 'date' ? 'active' : ''}`}
                onClick={() => setGroupBy('date')}
              >
                By date
              </button>
              <button
                type="button"
                className={`nexus-pulse-group-btn ${groupBy === 'company' ? 'active' : ''}`}
                onClick={() => setGroupBy('company')}
              >
                By company
              </button>
            </div>
            {filterCompany !== 'All companies' ? (
              <span className="nexus-pulse-company-pill">
                {filterCompany}
              </span>
            ) : null}
          </div>

          <div className="type-filters">
            {SOURCE_CHIPS.map((chip) => (
              <div
                key={chip.label}
                role="presentation"
                className={`type-chip ${filterSource === chip.label ? 'active' : ''}`}
                onClick={() => {
                  setFilterSource(chip.label);
                  setCurrentPage(1);
                }}
              >
                <div className="type-chip-dot" style={{ background: chip.color }} />
                {chip.label}
                <span className="type-chip-count">{getCountForSource(chip.label)}</span>
              </div>
            ))}
          </div>

          {loading && events.length === 0 ? (
            <div className="nexus-pulse-skeleton" aria-busy="true" aria-label="Loading pulse feed">
              {[1, 2, 3, 4, 5].map((n) => (
                <div key={n} className="nexus-pulse-skeleton-card" />
              ))}
            </div>
          ) : null}

          {!loading && events.length === 0 ? (
            <div className="nexus-pulse-empty">
              <Activity className="nexus-pulse-empty-pulse-icon" size={32} strokeWidth={1.5} aria-hidden />
              <h3>No pulse events yet</h3>
              <p>
                Unified events are built from SERFF, News/PR, and ad tables. If you recently
                ingested data, ask your admin to run event rebuild, or open Nexus View for the
                canvas summary.
              </p>
            </div>
          ) : null}

          <div className="table-area filings-table-panel nexus-pulse-table-panel">
            <div className="table-wrap">
              <table className="nexus-pulse-table">
                <thead>
                  <tr>
                    <th className="col-type">Source</th>
                    <th className="col-company">Company</th>
                    <th className="col-product-name">Event</th>
                    <th className="col-date">Activity</th>
                    <th className="col-actions" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(groupedEvents).map((groupKey) => (
                    <React.Fragment key={groupKey}>
                      <tr className="nexus-pulse-group-row">
                        <td colSpan={5}>
                          <div className="nexus-pulse-group-label">
                            <span className="nexus-pulse-group-dot" aria-hidden />
                            {groupKey}
                            <span className="nexus-pulse-group-count">
                              {groupedEvents[groupKey].length}
                              {' '}
                              events
                              {groupBy === 'company' ? ' · this page' : ''}
                            </span>
                          </div>
                        </td>
                      </tr>
                      {groupedEvents[groupKey].map((event) => (
                        <PulseEventRow key={event.id} event={event} navigate={navigate} />
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {totalRows > 0 ? (
            <IntelAxisTablePagination
              currentPage={currentPage}
              rowsPerPage={rowsPerPage}
              totalRows={totalRows}
              pageInput={pageInput}
              onPageInputChange={setPageInput}
              onPageInputSubmit={handlePageInputSubmit}
              onPageChange={setCurrentPage}
              onRowsPerPageChange={(n) => {
                setRowsPerPage(n);
                setCurrentPage(1);
              }}
            />
          ) : null}
        </div>
      </div>

      <MiTableFiltersDrawer
        isOpen={showTableFilters}
        onClose={() => setShowTableFilters(false)}
        filterCompany={filterCompany}
        setFilterCompany={setFilterCompany}
        companyCatalog={companyCatalog}
        setCurrentPage={setCurrentPage}
      />
    </>
  );
}
